// An address identifies a position in the base text. Every apparatus layer --
// variants, synopsis, manuscript images, commentaries, parallels -- resolves
// what it knows to one of these, which is what lets a single click drive all
// of them at once.
//
//   work      string    the loaded work ('Chullin', 'Mekhilta')
//   chapter   int       0-based
//   halakhah  int       0-based; the work's second structural level (halakhah,
//                       mishnah, parashah -- the name differs, the role doesn't)
//   baseIdx   int|null  word index within the chapter; null when whatever
//                       produced this address can only place it to the halakhah
//
// The nullable baseIdx is load-bearing, not laziness. Apparatus sources differ
// in how precisely they can be anchored, and they differ *entry by entry*, not
// source by source: of Chasdei David's 34,633 OCR'd comments, ~30,500 carry a
// dibbur hamatchil but only ~9,000 of those lemmas actually validated against
// the base text. Forcing a word anchor on the rest would either drop them or
// point them at the wrong word; a halakhah-scoped anchor is the honest answer,
// and the reader still gets the note in the right place, just not to the word.

export const WORD = 'word';
export const HALAKHAH = 'halakhah';
export const CHAPTER = 'chapter';

export function makeAddress(work, chapter, halakhah, baseIdx = null) {
    return {
        work: work == null ? null : String(work),
        chapter: toInt(chapter),
        halakhah: toInt(halakhah),
        baseIdx: baseIdx == null ? null : toInt(baseIdx),
    };
}

// How precisely does this address/anchor place itself? Layers use this to
// decide whether they may draw an inline word marker or only a halakhah-level
// one, and the dock uses it to decide what "the current note" even means.
export function scopeOf(addr) {
    if (!addr) return null;
    if (addr.baseIdx != null) return WORD;
    if (addr.halakhah != null) return HALAKHAH;
    if (addr.chapter != null) return CHAPTER;
    return null;
}

export function addressKey(addr) {
    if (!addr) return '';
    return [addr.work, addr.chapter, addr.halakhah, addr.baseIdx].join('|');
}

export function sameAddress(a, b) {
    if (!a || !b) return a === b;
    return addressKey(a) === addressKey(b);
}

// Drop to halakhah precision. Used when a word-precise selection has to be
// matched against halakhah-scoped entries.
export function coarsen(addr) {
    if (!addr) return null;
    return { ...addr, baseIdx: null };
}

// Does `anchor` (an entry's position) belong at `addr` (the reader's current
// selection)? Asymmetric on purpose: a halakhah-scoped entry is relevant to
// every word in its halakhah, but a word-scoped entry is only relevant to its
// own word. When the selection itself is halakhah-scoped, every entry in that
// halakhah is in scope -- that is the "show me this whole halakhah's notes"
// case, which is how the commentary docks open by default.
export function anchorMatches(anchor, addr) {
    if (!anchor || !addr) return false;
    if (anchor.work != null && addr.work != null && anchor.work !== addr.work) return false;
    if (anchor.chapter !== addr.chapter) return false;

    const anchorScope = scopeOf(anchor);
    if (anchorScope === CHAPTER) return true;
    if (anchor.halakhah !== addr.halakhah) return false;
    if (anchorScope === HALAKHAH) return true;
    // anchor is word-scoped
    if (addr.baseIdx == null) return true;      // halakhah-wide selection
    if (anchor.endIdx != null) {                // entries may span a run of words
        return addr.baseIdx >= anchor.baseIdx && addr.baseIdx <= anchor.endIdx;
    }
    return anchor.baseIdx === addr.baseIdx;
}

// Order entries the way a reader expects to meet them: by position, with
// halakhah-scoped entries (no word anchor) sorting ahead of the word-anchored
// ones inside the same halakhah -- an unplaced note is about the halakhah as a
// whole, so it reads as a preamble rather than being stranded mid-sequence.
export function compareAnchors(a, b) {
    if (a.chapter !== b.chapter) return a.chapter - b.chapter;
    if (a.halakhah !== b.halakhah) return (a.halakhah ?? -1) - (b.halakhah ?? -1);
    const ai = a.baseIdx, bi = b.baseIdx;
    if (ai == null && bi == null) return (a.order ?? 0) - (b.order ?? 0);
    if (ai == null) return -1;
    if (bi == null) return 1;
    if (ai !== bi) return ai - bi;
    return (a.order ?? 0) - (b.order ?? 0);
}

function toInt(v) {
    if (v == null) return null;
    const n = Number(v);
    return Number.isFinite(n) ? Math.trunc(n) : null;
}
